/**
 * Focus's launch (D75), and only Focus's: who the learner is, which attempt this is, which catalogue
 * entry is playing, and where its statements go — read off the address Focus frames the player with.
 *
 * `?slxapi=<JSON>&registration=<uuid>&component=<id>`:
 *
 * - `slxapi.actor`: the learner, as an xAPI Agent. Passed on as Focus wrote it.
 * - `slxapi.endpoint`: Focus's LRS base. The player's server posts to it, if it is allow-listed.
 * - `slxapi.auth`: the reporting token for that LRS, already `Bearer …`.
 * - `registration`: the attempt (`slxapi.registration` when present, else the parameter).
 * - `component`: Focus's id for the lesson — not read. It is the asset id, because Focus's
 *   catalogue is the studio's export, which names a lesson by its asset id (D77); the player names
 *   the activity by the asset id itself, as the export does, so the two cannot differ.
 *
 * `slxapi` also carries Focus's other services (`ai`, `chat`) with tokens of their own. They are not
 * the player's business, and are dropped here: they never reach the player's server.
 *
 * `slxapi.learner.track` (Focus 086): the learner's placement track, `pre_a1` | `basic` | `advanced`,
 * on a lesson that follows the track. Kept only to sign the speaking bot's pass (D78); anything
 * else is no track.
 *
 * Taken off the address as it is read: it is a credential, and does not belong in history, a reload
 * or a shared link. A reload of the player alone is therefore a guest (nothing sent); Focus launches
 * it afresh when its own page reloads.
 */

import type { LearnerTrack, ProviderLaunch } from '../types.js';
import { focusState } from './state.js';

const text = (value: unknown): string | null => (typeof value === 'string' && value.trim() !== '' ? value.trim() : null);

/** xAPI's registration, and the key of Focus's state, is a UUID; anything else is not one. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const uuid = (value: unknown): string | null => {
  const candidate = text(value);
  return candidate !== null && UUID.test(candidate) ? candidate : null;
};

const TRACKS: readonly LearnerTrack[] = ['pre_a1', 'basic', 'advanced'];

/** `slxapi.learner.track` when it is one of Focus's three, else null. */
export function trackOf(slxapi: Record<string, unknown>): LearnerTrack | null {
  const learner = slxapi['learner'];
  const track = typeof learner === 'object' && learner !== null ? (learner as Record<string, unknown>)['track'] : null;
  return TRACKS.find((known) => known === track) ?? null;
}

/** The JSON, whether the query value arrived decoded once (the usual case) or still encoded. */
function parse(raw: string): Record<string, unknown> | null {
  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    // a literal `%` in an already-decoded value: the raw form is the one
  }
  for (const candidate of [raw, decoded]) {
    try {
      const value: unknown = JSON.parse(candidate);
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) return value as Record<string, unknown>;
    } catch {
      // the next form, or none
    }
  }
  return null;
}

/** Focus's launch on the address, or null — and the address without `slxapi` either way. */
export function captureFocusLaunch(): ProviderLaunch | null {
  const params = new URLSearchParams(location.search);
  const raw = params.get('slxapi');
  if (raw === null) return null;

  params.delete('slxapi');
  const rest = params.toString();
  try {
    history.replaceState(history.state, '', `${location.pathname}${rest === '' ? '' : `?${rest}`}${location.hash}`);
  } catch {
    // The launch is still read; only the address keeps it.
  }

  const slxapi = parse(raw);
  if (slxapi === null) return null;
  // A learner is an actor with an account — what Focus's LRS keys a learner on.
  const actor = slxapi['actor'];
  const account = typeof actor === 'object' && actor !== null ? (actor as Record<string, unknown>)['account'] : null;
  const learner = typeof account === 'object' && account !== null ? text((account as Record<string, unknown>)['name']) : null;
  const endpoint = text(slxapi['endpoint']);
  if (learner === null || endpoint === null) return null;

  const registration = uuid(slxapi['registration']) ?? uuid(params.get('registration'));
  const lrs = { endpoint, auth: text(slxapi['auth']) };
  const track = trackOf(slxapi);
  return {
    actor: actor as Record<string, unknown>,
    registration,
    lrs,
    lrsName: "Focus's LRS",
    // Focus keys the kept attempt on the registration; without one there is nothing to key it on.
    ...(registration === null ? {} : { state: focusState(lrs, registration) }),
    ...(track === null ? {} : { track }),
  };
}
